import React from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import type { ColorSchemePreference } from '@invoiceflow/portal-core';

import { useThemeStore } from '../stores/useThemeStore';

const ORDER: ColorSchemePreference[] = ['light', 'dark', 'system'];

const ICON: Record<ColorSchemePreference, React.ComponentType<{ className?: string }>> = {
  light: Sun,
  dark: Moon,
  system: Monitor,
};

const LABEL: Record<ColorSchemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'Match device',
};

/**
 * Cycles light → dark → match device.
 *
 * Three states rather than a two-way switch, because "match device" is the
 * default and a customer who lands on it has no way back without a third stop.
 *
 * `role="link"` is not wanted here — this really is a button, and buttons are
 * hidden in print anyway (see the print rules in index.css).
 */
export const ThemeToggle: React.FC<{ className?: string }> = ({ className }) => {
  const preference = useThemeStore(state => state.preference);
  const setPreference = useThemeStore(state => state.setPreference);

  const next = ORDER[(ORDER.indexOf(preference) + 1) % ORDER.length] as ColorSchemePreference;
  const Icon = ICON[preference];

  return (
    <button
      type="button"
      onClick={() => setPreference(next)}
      title={`Appearance: ${LABEL[preference]}. Switch to ${LABEL[next]}.`}
      aria-label={`Appearance: ${LABEL[preference]}. Switch to ${LABEL[next]}.`}
      className={`theme-toggle inline-flex items-center justify-center w-9 h-9 rounded-lg text-content-muted hover:text-content hover:bg-surface-muted transition-colors ${className ?? ''}`}
    >
      <Icon className="w-5 h-5" />
    </button>
  );
};

export default ThemeToggle;
